import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../auth/AuthContext';
import { firstGrapheme } from '../services/displayName';
import AccountPanel from './AccountPanel';
import AuthModal from './AuthModal';

const PanelContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  position: relative;
`;

const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  background: rgba(20, 8, 36, 0.98);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  padding: 8px 0;
  min-width: 180px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
  z-index: 100;
`;

const DropdownItem = styled.button`
  width: 100%;
  text-align: left;
  padding: 10px 16px;
  background: none;
  border: none;
  color: #fff;
  font-size: 0.9rem;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
`;

const GhostButton = styled.button`
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: #fff;
  padding: 8px 16px;
  border-radius: 999px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.85rem;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(255, 255, 255, 0.4);
    transform: translateY(-1px);
  }
`;

const PrimaryButton = styled(GhostButton)`
  background: linear-gradient(135deg, #ff00aa 0%, #00f5ff 100%);
  border: none;
`;

const UserBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 999px;
  padding: 6px 10px 6px 6px;
`;

const Avatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ff00aa, #00f5ff);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.85rem;
  color: #fff;
`;

const UserMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.1;
`;

const UserName = styled.span`
  font-size: 0.85rem;
  font-weight: 600;
`;

const UserEmail = styled.span`
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.7);
`;

const LogoutButton = styled.button`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.75rem;
  cursor: pointer;
  padding: 0 6px 0 0;
`;

function AuthPanel({ onNavigateToSaved, onNavigateToRecommendations, onNavigateToCreate, onNavigateToMyUploads, onNavigateToReview, onNavigateToAbout, canCreatePost, canReview }) {
  const { user, authReady, signOut } = useAuth();
  const [modalOpen, setModalOpen] = useState(false);
  const [mode, setMode] = useState('login');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [dropdownOpen]);

  const displayName = user ? (user.displayName || user.email || 'Explorer') : '';

  const avatarLetter = displayName ? firstGrapheme(displayName) : 'U';

  if (!authReady) {
    return null;
  }

  return (
    <>
      <PanelContainer>
        {user ? (
          <div ref={dropdownRef} style={{ position: 'relative' }}>
            <UserBadge onClick={() => setDropdownOpen(!dropdownOpen)} style={{ cursor: 'pointer' }}>
              <Avatar>{avatarLetter}</Avatar>
              <UserMeta>
                <UserName>{displayName}</UserName>
                {user.email && <UserEmail>{user.email}</UserEmail>}
              </UserMeta>
            </UserBadge>
            {dropdownOpen && (
              <Dropdown>
                <DropdownItem type="button" onClick={() => { setAccountOpen(true); setDropdownOpen(false); }}>
                  Account
                </DropdownItem>
                {onNavigateToAbout && (
                  <DropdownItem type="button" onClick={() => { onNavigateToAbout(); setDropdownOpen(false); }}>
                    About Us
                  </DropdownItem>
                )}
                <DropdownItem type="button" onClick={() => { onNavigateToSaved?.(); setDropdownOpen(false); }}>
                  Saved
                </DropdownItem>
                <DropdownItem type="button" onClick={() => { onNavigateToRecommendations?.(); setDropdownOpen(false); }}>
                  Recommendations
                </DropdownItem>
                {onNavigateToMyUploads && (
                  <DropdownItem type="button" onClick={() => { onNavigateToMyUploads(); setDropdownOpen(false); }}>
                    My uploads
                  </DropdownItem>
                )}
                {onNavigateToCreate && (
                  <DropdownItem type="button" onClick={() => { onNavigateToCreate(); setDropdownOpen(false); }}>
                    Create post
                  </DropdownItem>
                )}
                {canReview && onNavigateToReview && (
                  <DropdownItem type="button" onClick={() => { onNavigateToReview(); setDropdownOpen(false); }}>
                    Review content
                  </DropdownItem>
                )}
                <DropdownItem type="button" onClick={() => { signOut(); setDropdownOpen(false); }}>
                  Sign out
                </DropdownItem>
              </Dropdown>
            )}
          </div>
        ) : (
          <>
            {onNavigateToAbout && (
              <GhostButton type="button" onClick={onNavigateToAbout}>
                About Us
              </GhostButton>
            )}
            <GhostButton type="button" onClick={() => { setMode('login'); setModalOpen(true); }}>
              Log In
            </GhostButton>
            <PrimaryButton type="button" onClick={() => { setMode('signup'); setModalOpen(true); }}>
              Sign Up
            </PrimaryButton>
          </>
        )}
      </PanelContainer>

      {accountOpen && user ? <AccountPanel onClose={() => setAccountOpen(false)} /> : null}
      <AuthModal
        open={modalOpen}
        mode={mode}
        onClose={() => setModalOpen(false)}
        onModeChange={setMode}
      />
    </>
  );
}

export default AuthPanel;
